/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit'
import * as API from '../../api'
import { DashboardVo } from '../../vo/Dashboard'
import { Dashboard } from '../../schemas/DashboardSchema'

interface ExportPdfParam {
  html: string
  filename: string
  windowWidth: number
  windowHeight: number
  pageHeight?: number
}

export const exportPdf = createAsyncThunk(
  'dashboard/exportPdf',
  async ({ html, filename, windowWidth, windowHeight, pageHeight }: ExportPdfParam) => {
    const response = await API.exportPdf(html, filename, windowWidth, windowHeight, pageHeight)
    // @ts-ignore
    return response.data
  }
)

function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
const MOCKED_DASHBOARDS = [
  {
    dashboardId: 1,
    dashboardName: '1a. 7-Eleven_Brand Analysis',
    createDate: '2021/03/16',
    author: 'yphuang',
    isEditing: false,
    layout: [
      { i: 'a', x: 0, y: 0, w: 9, h: 8, minW: 4, minH: 8 },
      { i: 'b', x: 9, y: 0, w: 3, h: 3, minW: 4, minH: 8 },
      { i: 'c', x: 9, y: 0, w: 3, h: 5, minW: 4, minH: 8 },
      { i: 'd', x: 0, y: 8, w: 6, h: 8, minW: 4, minH: 8 }
    ],
    widgets: [],
    status: 'idle', // idle|loading|succeeded|failed
    error: null
  },
  {
    dashboardId: 2,
    dashboardName: 'Electric Car Competitive Analysis',
    createDate: '2021/03/16',
    author: 'yphuang',
    isEditing: false,
    layout: [
      { i: 'a', x: 0, y: 0, w: 9, h: 9, minW: 2, minH: 4 },
      { i: 'b', x: 9, y: 0, w: 9, h: 9, minW: 2, minH: 4 },
      { i: 'c', x: 9, y: 2, w: 9, h: 9, minW: 2, minH: 4 },
      { i: 'd', x: 0, y: 8, w: 9, h: 9, minW: 2, minH: 4 },
      { i: 'e', x: 4, y: 8, w: 9, h: 9, minW: 2, minH: 4 },
      { i: 'f', x: 0, y: 0, w: 9, h: 9, minW: 2, minH: 4 }
    ],
    widgets: [],
    status: 'idle', // idle|loading|succeeded|failed
    error: null
  }
]

interface SaveDashboardParam {
  dashboardId: number
  layout: any
}

export const saveDashboard = createAsyncThunk(
  'dashboard/saveDashboard',
  async ({ dashboardId, layout }: SaveDashboardParam) => {
    await timeout(1000)

    const found = MOCKED_DASHBOARDS.find((dashboard) => dashboard.dashboardId === dashboardId)
    if (!found) {
      throw new Error('Not found.')
    }

    return {
      ...found,
      layout
    }
  }
)

type GetDashboardParam = Omit<SaveDashboardParam, 'layout'> & {
  isExportMode?: boolean
}

export const getDashboard = createAsyncThunk<
  Dashboard,
  GetDashboardParam,
  { rejectValue: API.FetchErrorResponse | undefined }
>('dashboard/getDashboard', async ({ dashboardId, isExportMode = false }, { rejectWithValue }) => {
  try {
    const { data } = await API.getDashboard(dashboardId)
    const transformedData = DashboardVo(data)
    // * refresh recent view API only when not in export mode
    if (!isExportMode) {
      await API.updateDashboardRecentView(dashboardId)
    }
    return transformedData
  } catch (err: unknown) {
    return rejectWithValue((err as API.FetchError)?.response?.data)
  }
})

export const deleteDashboard = createAsyncThunk(
  'dashboard/deleteDashboard',
  async (dashboardId: number, { rejectWithValue }) => {
    try {
      await API.deleteDashboard(dashboardId)
      return dashboardId
    } catch (err: unknown) {
      const apiError = (err as API.FetchError)?.response?.data
      return rejectWithValue({
        error: apiError?.error,
        status: apiError?.status
      })
    }
  }
)

export const copyDashboard = createAsyncThunk(
  'dashboard/copyDashboard',
  async (
    { dashboardId, dashboardName }: { dashboardId: number; dashboardName: string },
    { rejectWithValue }
  ) => {
    try {
      const newDashboardId = await API.copyDashboard(dashboardId, dashboardName)
      return newDashboardId
    } catch (err: unknown) {
      const apiError = (err as API.FetchError)?.response?.data
      return rejectWithValue({
        error: apiError?.error,
        info: apiError?.info || [],
        status: apiError?.status
      })
    }
  }
)
